/* ==========================================================================
   MOTIV8BUILD theme.js — vanilla, dependency-free.
   Everything here is progressive enhancement: forms and links work without it.
   ========================================================================== */
(() => {
  'use strict';

  const root = document.documentElement;
  const M8B = (window.M8B = window.M8B || {});
  const strings = M8B.strings || {};
  const routes = M8B.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update', cart_url: '/cart', predictive_search_url: '/search/suggest' };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.dataset.motion === 'off';
  const isCinematic = () => root.dataset.motion === 'cinematic' && !reducedMotion();
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Utilities ---------- */
  M8B.formatMoney = (cents, format = M8B.moneyFormat || '${{amount}}') => {
    const value = (Number(cents) / 100);
    const fmt = (n, dec, thou = ',', d = '.') => {
      const [i, f] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (f ? d + f : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        default: return fmt(value, 2);
      }
    });
  };

  const toast = (() => {
    let el, timer;
    return (msg, type = 'ok') => {
      if (!el) {
        el = document.createElement('div');
        el.className = 'toast';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        document.body.appendChild(el);
      }
      el.textContent = msg;
      el.classList.toggle('toast--error', type === 'error');
      el.classList.add('is-visible');
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('is-visible'), 3200);
    };
  })();
  M8B.toast = toast;

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Dialog (drawers / modals) with animated close ---------- */
  const Dialogs = {
    open(dialog, opener) {
      if (!dialog || dialog.open) return;
      dialog._opener = opener || document.activeElement;
      dialog.classList.remove('is-closing');
      dialog.showModal();
      root.style.overflow = 'hidden';
      dialog.dispatchEvent(new CustomEvent('dialog:open'));
    },
    close(dialog) {
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
      const done = () => {
        dialog.classList.remove('is-closing');
        dialog.close();
        root.style.overflow = '';
        if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
      };
      if (reducedMotion()) return done();
      dialog.classList.add('is-closing');
      setTimeout(done, 380);
    },
  };
  M8B.dialogs = Dialogs;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog && typeof dialog.showModal === 'function') {
        e.preventDefault();
        Dialogs.open(dialog, opener);
      }
      return;
    }
    const closer = e.target.closest('[data-close-dialog]');
    if (closer) {
      e.preventDefault();
      Dialogs.close(closer.closest('dialog'));
      return;
    }
    // click on backdrop (the dialog element itself, outside the panel)
    if (e.target.matches('dialog.drawer, dialog.modal')) Dialogs.close(e.target);
  });
  document.addEventListener('cancel', (e) => {
    if (e.target.matches('dialog.drawer, dialog.modal')) { e.preventDefault(); Dialogs.close(e.target); }
  }, true);

  /* ---------- Header: scrolled state + hide on scroll ---------- */
  const header = $('[data-site-header]');
  if (header) {
    let lastY = window.scrollY;
    let ticking = false;
    const hideOnScroll = header.dataset.hideOnScroll === 'true';
    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      if (hideOnScroll) {
        const anyOpen = header.querySelector('details[open]');
        header.classList.toggle('is-hidden', y > lastY && y > 320 && !anyOpen);
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();

    // Mega menus: close others, close on outside click / Escape
    const megas = $$('details[data-mega]', header);
    megas.forEach((d) => d.addEventListener('toggle', () => {
      if (d.open) megas.forEach((o) => o !== d && (o.open = false));
      header.classList.toggle('is-open', megas.some((m) => m.open));
    }));
    document.addEventListener('click', (e) => { if (!header.contains(e.target)) megas.forEach((d) => (d.open = false)); });
    // Warm up menu card images as soon as the visitor reaches for the navigation
    const warm = () => $$('.mega img[loading="lazy"]', header).forEach((img) => (img.loading = 'eager'));
    header.addEventListener('pointerenter', warm, { once: true });
    header.addEventListener('focusin', warm, { once: true });
    // Desktop: open on hover with a short intent delay; click/keyboard still work
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      let openTimer, closeTimer;
      megas.forEach((d) => {
        const li = d.parentElement;
        li.addEventListener('mouseenter', () => { clearTimeout(closeTimer); clearTimeout(openTimer); openTimer = setTimeout(() => { d.open = true; }, 90); });
        li.addEventListener('mouseleave', () => { clearTimeout(openTimer); });
      });
      header.addEventListener('mouseleave', () => { clearTimeout(openTimer); closeTimer = setTimeout(() => megas.forEach((d) => (d.open = false)), 220); });
      header.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      // Hovering a plain top-level link closes any open menu
      $$('.main-nav > li > a', header).forEach((a) => a.addEventListener('mouseenter', () => megas.forEach((d) => (d.open = false))));
    }
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = megas.find((d) => d.open);
      if (open) { open.open = false; open.querySelector('summary').focus(); }
    });
  }

  /* ---------- Reveal on scroll ---------- */
  const revealIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-in'); revealIO.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }) : null;
  M8B.observeReveals = (ctx = document) => {
    $$('[data-reveal]:not(.is-in)', ctx).forEach((el) => {
      if (!revealIO || reducedMotion()) el.classList.add('is-in');
      else if (!el.closest('[data-reveal-manual]')) revealIO.observe(el);
    });
  };
  M8B.observeReveals();
  // The hero reveals on load (or when the intro finishes) — never waits on scroll.
  const hero = $('[data-hero]');
  if (hero && !root.classList.contains('intro-pending')) {
    requestAnimationFrame(() => { hero.classList.add('is-ready'); $$('[data-reveal]', hero).forEach((el) => el.classList.add('is-in')); });
  }

  /* ---------- Cosmos: lightweight particle field ---------- */
  class CosmosField extends HTMLElement {
    connectedCallback() {
      if (reducedMotion() || root.dataset.motion === 'off') return;
      const lowPower = (navigator.hardwareConcurrency || 4) <= 2 || (navigator.connection && navigator.connection.saveData);
      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.density = Number(this.dataset.density || 0.00009) * (lowPower ? 0.4 : 1);
      this.resize = this.resize.bind(this);
      this.tick = this.tick.bind(this);
      this.resize();
      window.addEventListener('resize', this.resize, { passive: true });
      this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; if (this.visible) this.start(); });
      this.io.observe(this);
    }
    disconnectedCallback() { window.removeEventListener('resize', this.resize); this.io && this.io.disconnect(); cancelAnimationFrame(this.raf); }
    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = this.getBoundingClientRect();
      this.w = width; this.h = height;
      this.canvas.width = width * dpr; this.canvas.height = height * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(220, Math.round(width * height * this.density));
      this.stars = Array.from({ length: count }, () => ({
        x: Math.random() * width, y: Math.random() * height,
        r: Math.random() * 1.1 + 0.2, a: Math.random() * 0.6 + 0.15,
        tw: Math.random() * Math.PI * 2, s: Math.random() * 0.012 + 0.003,
        vy: -(Math.random() * 0.06 + 0.01), hue: Math.random() < 0.12 ? 'violet' : 'white',
      }));
    }
    start() { if (!this.running) { this.running = true; this.raf = requestAnimationFrame(this.tick); } }
    tick() {
      if (!this.visible) { this.running = false; return; }
      const { ctx, w, h } = this;
      ctx.clearRect(0, 0, w, h);
      for (const p of this.stars) {
        p.tw += p.s; p.y += p.vy;
        if (p.y < -2) { p.y = h + 2; p.x = Math.random() * w; }
        const alpha = p.a * (0.55 + 0.45 * Math.sin(p.tw));
        ctx.fillStyle = p.hue === 'violet' ? `rgba(150,140,220,${alpha})` : `rgba(235,236,240,${alpha})`;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      }
      this.raf = requestAnimationFrame(this.tick);
    }
  }
  customElements.define('cosmos-field', CosmosField);

  /* ---------- Cursor-following ambient light (desktop only) ---------- */
  const light = $('[data-cursor-light]');
  if (light && window.matchMedia('(hover: hover) and (pointer: fine)').matches && !reducedMotion()) {
    let x = 0, y = 0, cx = 0, cy = 0, raf;
    const loop = () => {
      cx += (x - cx) * 0.12; cy += (y - cy) * 0.12;
      light.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      raf = Math.abs(x - cx) + Math.abs(y - cy) > 0.5 ? requestAnimationFrame(loop) : null;
    };
    window.addEventListener('pointermove', (e) => {
      x = e.clientX; y = e.clientY; light.classList.add('is-active');
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });
    document.addEventListener('pointerleave', () => light.classList.remove('is-active'));
  }

  /* ---------- Depth / parallax on hero layers ---------- */
  const depthLayers = $$('[data-depth]');
  if (depthLayers.length && !reducedMotion() && root.dataset.motion !== 'subtle') {
    const fine = window.matchMedia('(pointer: fine)').matches;
    let ticking = false;
    const apply = (mx, my) => depthLayers.forEach((el) => {
      const d = Number(el.dataset.depth) || 0.02;
      const sy = window.scrollY * d * 4;
      el.style.transform = `translate3d(${mx * d * 100}px, ${my * d * 100 + sy}px, 0)`;
    });
    let mx = 0, my = 0;
    if (fine) window.addEventListener('pointermove', (e) => {
      mx = e.clientX / window.innerWidth - 0.5; my = e.clientY / window.innerHeight - 0.5;
      if (!ticking) { ticking = true; requestAnimationFrame(() => { apply(mx, my); ticking = false; }); }
    }, { passive: true });
    window.addEventListener('scroll', () => {
      if (window.scrollY > window.innerHeight * 1.2) return;
      if (!ticking) { ticking = true; requestAnimationFrame(() => { apply(mx, my); ticking = false; }); }
    }, { passive: true });
  }

  /* ---------- Marquee pause control (WCAG 2.2.2) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-marquee-toggle]');
    if (!btn) return;
    const track = btn.closest('.marquee').querySelector('.marquee__track');
    const paused = btn.getAttribute('aria-pressed') === 'true';
    btn.setAttribute('aria-pressed', String(!paused));
    track.style.animationPlayState = paused ? 'running' : 'paused';
  });

  /* ---------- Manifesto lines light up as they cross the viewport center ---------- */
  $$('[data-manifesto]').forEach((wrap) => {
    if (reducedMotion() || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.classList.toggle('is-lit', e.isIntersecting || e.boundingClientRect.top < 0));
    }, { rootMargin: '-35% 0px -45% 0px' });
    $$('.manifesto__line', wrap).forEach((l) => io.observe(l));
  });

  /* ---------- Signature statement ---------- */
  $$('[data-signature]').forEach((el) => {
    if (!('IntersectionObserver' in window)) return el.classList.add('is-in');
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('is-in'); io.disconnect(); } }, { threshold: 0.45 });
    io.observe(el);
  });

  /* ---------- Front/back flip ---------- */
  $$('[data-flip]').forEach((wrap) => {
    const flip = $('.flip', wrap);
    const buttons = $$('[data-flip-to]', wrap);
    const status = $('[data-flip-status]', wrap);
    const set = (side) => {
      flip.classList.toggle('is-back', side === 'back');
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.flipTo === side)));
      $$('.flip__face', flip).forEach((f) => f.setAttribute('aria-hidden', String(!f.classList.contains(`flip__face--${side}`))));
      if (status) status.textContent = side === 'back' ? (strings.backView || 'Back view') : (strings.frontView || 'Front view');
    };
    buttons.forEach((b) => b.addEventListener('click', () => set(b.dataset.flipTo)));
    let sx = null;
    flip.addEventListener('pointerdown', (e) => { sx = e.clientX; });
    flip.addEventListener('pointerup', (e) => {
      if (sx === null) return;
      const dx = e.clientX - sx; sx = null;
      if (Math.abs(dx) > 40) set(flip.classList.contains('is-back') ? 'front' : 'back');
    });
    flip.addEventListener('click', (e) => { if (e.pointerType !== 'mouse' && e.detail) return; });
    set('front');
  });

  /* ---------- Mindset chapters: hovered/focused chapter drives the sticky visual ---------- */
  $$('[data-chapters]').forEach((wrap) => {
    const items = $$('[data-chapter-index]', wrap);
    const visuals = $$('.chapters-visual__item', wrap);
    const activate = (i) => visuals.forEach((v, n) => v.classList.toggle('is-active', n === i));
    items.forEach((item) => {
      const i = Number(item.dataset.chapterIndex);
      item.addEventListener('mouseenter', () => activate(i));
      item.addEventListener('focusin', () => activate(i));
    });
  });

  /* ==========================================================================
     COMMERCE
     ========================================================================== */

  /* ---------- Cart API wrapper ---------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer', 'cart-icon-bubble'];
      const page = $('[data-main-cart]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },
    async request(url, body) {
      const res = await fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || strings.cartError || 'Something went wrong.');
        err.data = data;
        throw err;
      }
      return data;
    },
    async addFromForm(form) {
      const fd = new FormData(form);
      const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }];
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v) props[m[1]] = v;
      }
      if (Object.keys(props).length) items[0].properties = props;
      const sellingPlan = fd.get('selling_plan');
      if (sellingPlan) items[0].selling_plan = Number(sellingPlan);
      const data = await this.request(routes.cart_add_url, { items });
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'add', data } }));
      return data;
    },
    async change(line, quantity) {
      const data = await this.request(routes.cart_change_url, { line, quantity });
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'change', data } }));
      return data;
    },
    async discount(codes) {
      const data = await this.request(routes.cart_update_url, { discount: codes.join(',') });
      this.renderSections(data.sections);
      return data;
    },
    async refresh() {
      const res = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`);
      if (res.ok) this.renderSections(await res.json());
    },
    renderSections(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-icon-bubble') {
          const fresh = doc.querySelector('[data-cart-count]');
          $$('[data-cart-count]').forEach((el) => { el.textContent = fresh ? fresh.textContent : ''; });
          const label = doc.querySelector('[data-cart-label]');
          $$('[data-cart-label]').forEach((el) => { if (label) el.textContent = label.textContent; });
          return;
        }
        const target = document.querySelector(`[data-cart-section="${id}"]`);
        const source = doc.querySelector(`[data-cart-section="${id}"]`);
        if (target && source) {
          const scroll = target.querySelector('.drawer__body')?.scrollTop;
          target.innerHTML = source.innerHTML;
          if (scroll) { const b = target.querySelector('.drawer__body'); if (b) b.scrollTop = scroll; }
        }
      });
    },
  };
  M8B.cart = Cart;

  const cartDrawer = () => document.getElementById('CartDrawer');
  const openCart = (opener) => {
    const d = cartDrawer();
    if (d && root.dataset.cartType === 'drawer') { Dialogs.open(d, opener); return true; }
    return false;
  };
  // Header cart icon opens the drawer (link still goes to /cart without JS)
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-toggle]');
    if (link && openCart(link)) e.preventDefault();
  });

  /* ---------- Product form (PDP, quick add) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = e.submitter && e.submitter.matches('[type="submit"]') ? e.submitter : form.querySelector('[type="submit"]');
    if (form.dataset.busy === 'true') return; // prevent duplicate submissions
    if (e.submitter && e.submitter.name === 'id') {
      // quick-add size buttons carry the variant id themselves
      form.querySelector('input[name="id"]').value = e.submitter.value;
    }
    const errorBox = form.querySelector('[data-form-error]') || form.closest('[data-product-root]')?.querySelector('[data-form-error]');
    if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
    form.dataset.busy = 'true';
    btn && btn.classList.add('is-loading');
    btn && btn.setAttribute('aria-busy', 'true');
    try {
      await Cart.addFromForm(form);
      if (!openCart(btn)) {
        if (root.dataset.cartType === 'page') { window.location.href = routes.cart_url; return; }
      }
      toast(strings.added || 'Added to cart');
    } catch (err) {
      const msg = err.message || strings.cartError;
      if (errorBox) { errorBox.textContent = msg; errorBox.hidden = false; }
      toast(msg, 'error');
    } finally {
      form.dataset.busy = 'false';
      btn && btn.classList.remove('is-loading');
      btn && btn.removeAttribute('aria-busy');
    }
  });

  /* ---------- Cart drawer + page interactions (delegated) ---------- */
  let cartBusy = false;
  const withCartBusy = async (container, fn) => {
    if (cartBusy) return;
    cartBusy = true;
    container && container.setAttribute('aria-busy', 'true');
    container && container.classList.add('is-loading');
    try { await fn(); } catch (err) { toast(err.message, 'error'); await Cart.refresh(); }
    finally { cartBusy = false; container && container.removeAttribute('aria-busy'); container && container.classList.remove('is-loading'); }
  };
  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-cart-qty]');
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (!qtyBtn && !removeBtn) return;
    e.preventDefault();
    const item = (qtyBtn || removeBtn).closest('[data-line]');
    const line = Number(item.dataset.line);
    const input = item.querySelector('input[data-qty-input]');
    let qty = 0;
    if (qtyBtn) qty = Math.max(0, Number(input.value) + Number(qtyBtn.dataset.cartQty));
    withCartBusy(item.closest('[data-cart-section]'), async () => {
      await Cart.change(line, qty);
      if (qty === 0) toast(strings.removed || 'Removed from cart');
    });
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-qty-input]');
    if (!input || !input.closest('[data-cart-section]')) return;
    const item = input.closest('[data-line]');
    withCartBusy(item.closest('[data-cart-section]'), () => Cart.change(Number(item.dataset.line), Math.max(0, Number(input.value) || 0)));
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = input.value.trim();
    if (!code) return;
    const existing = $$('[data-discount-code]', form.closest('[data-cart-section]')).map((el) => el.dataset.discountCode);
    withCartBusy(form.closest('[data-cart-section]'), async () => {
      const data = await Cart.discount([...new Set([...existing, code])]);
      const applied = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      toast(applied && applied.applicable ? (strings.discountApplied || 'Code applied') : (strings.discountInvalid || 'This code cannot be applied to your cart'), applied && applied.applicable ? 'ok' : 'error');
    });
  });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    const section = btn.closest('[data-cart-section]');
    const remaining = $$('[data-discount-code]', section).map((el) => el.dataset.discountCode).filter((c) => c !== btn.dataset.discountRemove);
    withCartBusy(section, () => Cart.discount(remaining));
  });
  // Keep drawer in sync if cart changes in another tab
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && cartDrawer()) Cart.refresh(); });

  /* ---------- Variant picker (PDP) ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]');
      const json = this.querySelector('script[type="application/json"]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.form = this.root.querySelector('form[data-product-form]');
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    selectedOptions() {
      return $$('fieldset[data-option-position]', this).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    findVariant(opts) {
      return this.product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    onChange() {
      const opts = this.selectedOptions();
      $$('fieldset[data-option-position]', this).forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = opts[i] || '';
      });
      const variant = this.findVariant(opts);
      this.updateAvailability();
      this.render(variant);
    }
    updateAvailability() {
      const opts = this.selectedOptions();
      $$('fieldset[data-option-position]', this).forEach((fs, pos) => {
        $$('input', fs).forEach((input) => {
          const test = [...opts]; test[pos] = input.value;
          // available if some variant matching higher-priority selections + this value is available
          const match = this.product.variants.filter((v) => v.options[pos] === input.value && v.options.every((o, i) => i === pos || i > pos || o === test[i]));
          const available = match.some((v) => v.available);
          input.classList.toggle('is-unavailable', !available);
          const lab = input.nextElementSibling;
          if (lab) {
            const sr = lab.querySelector('.visually-hidden[data-unavail]');
            if (!available && !sr) lab.insertAdjacentHTML('beforeend', `<span class="visually-hidden" data-unavail> — ${strings.unavailable || 'Currently unavailable'}</span>`);
            if (available && sr) sr.remove();
          }
        });
      });
    }
    render(variant) {
      const r = this.root;
      const idInput = this.form && this.form.querySelector('input[name="id"]');
      const select = r.querySelector('select[data-variant-select]');
      const addBtns = $$('[data-add-button]', r);
      const priceEls = $$('[data-price]', r);
      const stock = r.querySelector('[data-stock]');
      const variantLabel = $$('[data-variant-label]', r);
      if (!variant) {
        addBtns.forEach((b) => { b.disabled = true; b.querySelector('[data-add-label]').textContent = strings.unavailable || 'Currently unavailable'; });
        if (stock) { stock.dataset.state = 'out'; stock.textContent = strings.unavailable || 'Currently unavailable'; }
        r.classList.add('is-unavailable-combo');
        return;
      }
      r.classList.remove('is-unavailable-combo');
      if (idInput) idInput.value = variant.id;
      if (select) select.value = variant.id;
      $$('form[data-product-form] input[name="id"]', r).forEach((i) => (i.value = variant.id));
      priceEls.forEach((el) => {
        const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.innerHTML = `<span class="price__current">${M8B.formatMoney(variant.price)}</span>${sale ? `<s><span class="visually-hidden">${strings.regularPrice || 'Regular price'}</span>${M8B.formatMoney(variant.compare_at_price)}</s>` : ''}`;
        el.classList.toggle('price--sale', !!sale);
      });
      addBtns.forEach((b) => {
        b.disabled = !variant.available;
        b.querySelector('[data-add-label]').textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Currently unavailable');
      });
      variantLabel.forEach((el) => (el.textContent = variant.title));
      if (stock) {
        const q = variant.inventory_quantity;
        const tracked = variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny';
        let state = 'in', text = strings.inStock || 'In stock';
        if (!variant.available) { state = 'out'; text = strings.soldOut || 'Currently unavailable'; }
        else if (tracked && typeof q === 'number' && q > 0 && q <= Number(stock.dataset.lowThreshold || 0)) { state = 'low'; text = (strings.lowStock || 'Only [count] left').replace('[count]', q); }
        else if (stock.dataset.madeToOrder === 'true') { text = strings.madeToOrder || 'Printed to order'; }
        stock.dataset.state = state; stock.textContent = text;
      }
      // media: jump to variant image
      if (variant.featured_media && variant.featured_media.id) {
        const slide = r.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (slide) {
          const track = slide.parentElement;
          if (window.matchMedia('(max-width: 989px)').matches) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
          else if (slide !== track.firstElementChild) track.prepend(slide);
        }
      }
      // URL
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      r.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product gallery (mobile swipe counter/dots, desktop lightbox) ---------- */
  $$('[data-gallery]').forEach((g) => {
    const track = $('.pdp-gallery__track', g);
    const dots = $$('.pdp-gallery__dots button', g);
    const counter = $('[data-gallery-counter]', g);
    const slides = () => $$('.pdp-gallery__item', track);
    const sync = () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      if (counter) counter.textContent = `${i + 1} / ${slides().length}`;
    };
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    dots.forEach((d, n) => d.addEventListener('click', () => track.scrollTo({ left: n * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' })));
    const lightbox = document.getElementById(g.dataset.lightbox || '');
    if (lightbox) {
      track.addEventListener('click', (e) => {
        const item = e.target.closest('.pdp-gallery__item');
        if (!item || !window.matchMedia('(min-width: 990px)').matches) return;
        const img = item.querySelector('img');
        if (!img) return;
        const body = $('[data-lightbox-body]', lightbox);
        body.innerHTML = '';
        slides().forEach((s) => { const i = s.querySelector('img'); if (i) { const c = i.cloneNode(); c.removeAttribute('sizes'); c.loading = 'lazy'; body.appendChild(c); } });
        Dialogs.open(lightbox, item);
        const idx = slides().filter((s) => s.querySelector('img')).indexOf(item);
        requestAnimationFrame(() => body.children[idx] && body.children[idx].scrollIntoView());
      });
    }
  });

  /* ---------- Sticky add-to-cart (mobile) ---------- */
  $$('[data-sticky-atc]').forEach((bar) => {
    const target = document.getElementById(bar.dataset.watch);
    if (!target || !('IntersectionObserver' in window)) return;
    const footer = $('.site-footer');
    let mainVisible = true, footerVisible = false;
    const update = () => bar.classList.toggle('is-visible', !mainVisible && !footerVisible && target.getBoundingClientRect().top < 0);
    new IntersectionObserver(([e]) => { mainVisible = e.isIntersecting; update(); }).observe(target);
    if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; update(); }).observe(footer);
    bar.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
      const form = document.getElementById(bar.dataset.form);
      if (form) form.requestSubmit(form.querySelector('[type="submit"]'));
    });
  });

  /* ---------- Product card: color swatches + quick add sizes ---------- */
  class ProductCard extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-card-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.sizesEl = this.querySelector('[data-quick-sizes]');
      this.img = this.querySelector('.card__img--main');
      this.color = this.data.default_color;
      this.querySelectorAll('[data-card-color]').forEach((btn) => btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.color = btn.dataset.cardColor;
        this.querySelectorAll('[data-card-color]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        const v = this.data.variants.find((x) => x.color === this.color && x.image);
        if (v && this.img) { this.img.srcset = ''; this.img.src = v.image; }
        this.renderSizes();
      }));
      this.renderSizes();
    }
    renderSizes() {
      if (!this.sizesEl) return;
      const vs = this.data.color_index === null ? this.data.variants : this.data.variants.filter((v) => v.color === this.color);
      this.sizesEl.innerHTML = vs.map((v) => `<button type="submit" name="id" value="${v.id}" ${v.available ? '' : 'disabled'} aria-label="${strings.addToCart || 'Add to cart'}: ${this.data.title} — ${v.title}${v.available ? '' : ' (' + (strings.soldOut || 'unavailable') + ')'}">${v.size || v.title}</button>`).join('');
    }
  }
  customElements.define('product-card', ProductCard);

  /* ---------- Facets: progressive filtering with Section Rendering API ---------- */
  class FacetsForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.sectionId = this.dataset.sectionId;
      this.debounced = this.debounce(() => this.submit(), 450);
      this.form.addEventListener('change', (e) => { if (!e.target.matches('[data-no-auto]')) this.debounced(); });
      this.form.addEventListener('input', (e) => { if (e.target.type === 'number') this.debounced(); });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      document.addEventListener('click', (e) => {
        const a = e.target.closest('[data-facet-link]');
        if (!a) return;
        e.preventDefault();
        this.load(new URL(a.href, window.location.origin).searchParams.toString());
      });
      window.addEventListener('popstate', (e) => { if (e.state && e.state.facets !== undefined) this.load(e.state.facets, false); });
    }
    debounce(fn, ms) { let t; return () => { clearTimeout(t); t = setTimeout(fn, ms); }; }
    params() {
      const fd = new FormData(this.form);
      // also pull sort from toolbar form linked via form="" attribute
      const sp = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') sp.append(k, v);
      return sp.toString();
    }
    submit() { this.load(this.params()); }
    async load(qs, push = true) {
      const grid = document.getElementById(`ResultsWrap-${this.sectionId}`);
      grid && grid.classList.add('is-loading');
      try {
        const res = await fetch(`${window.location.pathname}?${qs}${qs ? '&' : ''}section_id=${this.sectionId}`);
        const doc = parseHTML(await res.text());
        ['ResultsWrap', 'FacetsBody', 'ActiveFilters', 'ProductCount'].forEach((key) => {
          const id = `${key}-${this.sectionId}`;
          const fresh = doc.getElementById(id), cur = document.getElementById(id);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        if (push) window.history.pushState({ facets: qs }, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
        M8B.observeReveals(document.getElementById(`ResultsWrap-${this.sectionId}`) || document);
        const live = document.getElementById(`ProductCount-${this.sectionId}`);
        live && live.setAttribute('aria-live', 'polite');
      } catch (err) {
        window.location.search = qs; // fall back to a full page load
      } finally {
        grid && grid.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facets-form', FacetsForm);
  // Sort select is associated with the facets form via form="" (works without JS via Apply)
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('select[data-sort]');
    if (!sel) return;
    const facets = document.querySelector('facets-form');
    if (facets && facets.form) facets.submit();
    else { const url = new URL(window.location.href); url.searchParams.set('sort_by', sel.value); window.location.href = url.toString(); }
  });

  /* ---------- Product recommendations (lazy, Section Rendering) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) return this.load();
      const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); this.load(); } }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; M8B.observeReveals(this); }
      } catch (_) { /* recommendations are optional */ }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      if (!this.input || !this.results) return;
      let t;
      this.input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.search(), 260); });
      this.input.addEventListener('keydown', (e) => this.keys(e));
      this.closest('dialog')?.addEventListener('dialog:open', () => setTimeout(() => this.input.focus(), 50));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.hidden = true; this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      try {
        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=8&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const inner = doc.querySelector('[data-predictive-inner]');
        this.results.innerHTML = inner ? inner.innerHTML : '';
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        const count = this.results.querySelectorAll('[role="option"]').length;
        if (this.status) this.status.textContent = count ? (strings.resultsCount || '[count] results').replace('[count]', count) : (strings.noResults || 'No results');
      } catch (err) {
        if (err.name !== 'AbortError') this.results.hidden = true;
      }
    }
    keys(e) {
      const opts = $$('[role="option"]', this.results);
      if (!opts.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const cur = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') { if (cur > -1) { e.preventDefault(); opts[cur].click(); } return; }
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? (cur + 1) % opts.length : (cur - 1 + opts.length) % opts.length;
      opts.forEach((o, i) => o.setAttribute('aria-selected', String(i === next)));
      this.input.setAttribute('aria-activedescendant', opts[next].id);
      opts[next].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Newsletter forms: keep the visitor in place after posting ---------- */
  // Shopify customer forms redirect with ?customer_posted=true#anchor; we scroll to the anchor on load.
  if (window.location.search.includes('customer_posted=true') && window.location.hash) {
    const el = document.querySelector(window.location.hash);
    el && el.scrollIntoView({ block: 'center' });
  }

  /* ---------- Replay intro from any page ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-replay-intro]');
    if (!btn) return;
    try { localStorage.removeItem('m8b:intro-seen'); } catch (_) {}
    if (M8B.playIntro) { e.preventDefault(); M8B.playIntro(true); }
  });
})();
